import { dropDownFilterOptions, getFacilitySearchLookups } from '@AMIEWEB/GlobalSearch/helper';
import { getFilteredCities, upgradeBasedOnSearchOption } from 'app/components/GlobalSearch/Candidate/FilterUtils';
import { AutocompleteChangeReason } from '@mui/material';
import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import { getSearchLookups } from 'app/services/SharedServices/SharedServices';
import i18next from 'i18next';
import { IChip } from 'app/ComponentLibrary/Filter/ChipsContainer';

export enum SearchOrderFilterFieldNames {
  oid = 'orderIds',
  facility = 'facilityIds',
  division = 'divisionIds',
  discipline = 'disciplineIds',
  specialty = 'specialtyIds',
  subSpecialty = 'subSpecialtyIds',
  facilityStatus = 'facilityStatusIds',
  orderType = 'orderTypeIds',
  orderPriority = 'orderPriorityIds',
  startDateRange = 'orderStartDateRange',
  orderAgeDateRange = 'dateEnteredRange',
  orderEndDateRange = 'endDateRange',
  positions = 'positions',
  state = 'states',
  city = 'city',
  paysGww = 'paysGww',
  mfs = 'maxFilesSentId',
  shift = 'shiftIds',
  accountManager = 'amUserIds',
}

export interface ISelection {
  id: number;
  name: string;
  value: string;
  labelPrefix?: string;
  labelPostfix?: string;
}

export const MFSOpts = [
  { id: 0, value: 1, name: 'Yes', labelPrefix: 'MFS' },
  { id: 1, value: 2, name: 'No', labelPrefix: 'MFS' },
];
export const PaysGwwOpts = [
  { id: 0, value: true, name: 'Yes', labelPrefix: 'GWW' },
  { id: 1, value: false, name: 'No', labelPrefix: 'GWW' },
];

export const positionOpts = [
  {
    id: 0,
    value: { min: 1, max: 999 },
    name: '>0',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 1,
    value: { min: 1, max: 10 },
    name: '1-10',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 2,
    value: { min: 10, max: 50 },
    name: '10-50',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 3,
    value: { min: 50, max: 99 },
    name: '50-99',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
] as unknown as ISelection[];

export class SearchOrderFilterSpecifications {
  constructor(public filterSpecs: any, public gridStateFilters: any) {}

  getSearchOrderFilterSpecifications = () => {
    return [
      {
        filterName: i18next.t('search.globalSearch.placement.filterNames.orderId'),
        name: SearchOrderFilterFieldNames.oid,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: async key => getSearchLookups('OID', key),
        isMultiSelect: true,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.facility'),
        name: SearchOrderFilterFieldNames.facility,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: getFacilitySearchLookups,
        isMultiSelect: true,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.division'),
        name: SearchOrderFilterFieldNames.division,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs?.divisionOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.placement.filterNames.discipline'),
        name: SearchOrderFilterFieldNames.discipline,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.disciplineOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.placement.filterNames.specialty'),
        name: SearchOrderFilterFieldNames.specialty,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.specialtiesOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.subSpecialty'),
        name: SearchOrderFilterFieldNames.subSpecialty,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.subSpecialtiesOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.facilityStatus'),
        name: SearchOrderFilterFieldNames.facilityStatus,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.facilityStatusesOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.orderType'),
        name: SearchOrderFilterFieldNames.orderType,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.orderTypesOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.orderPriority'),
        name: SearchOrderFilterFieldNames.orderPriority,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.orderPrioritiesOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.orderAgeDateRange'),
        name: SearchOrderFilterFieldNames.orderAgeDateRange,
        placeholder: i18next.t('search.globalSearch.order.filterNames.orderAgeDateRange'),
        version3: true,
        type: FilterCompTypes.DATERANGEV2,
        variant: 'outlined',
        secondaryLabel: i18next.t('search.globalSearch.order.filterNames.orderAgeDateRange'),
        useMaxWidth: true,
        maxWidth: 210,
        clickAway: true,
        disablePortal: true,
        quickSelect: false,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.startDateRange'),
        name: SearchOrderFilterFieldNames.startDateRange,
        placeholder: i18next.t('search.globalSearch.order.filterNames.startDateRange'),
        version3: true,
        type: FilterCompTypes.DATERANGEV2,
        variant: 'outlined',
        secondaryLabel: i18next.t('search.globalSearch.order.filterNames.startDateRange'),
        useMaxWidth: true,
        maxWidth: 210,
        clickAway: true,
        disablePortal: true,
        quickSelect: false,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.orderEndDateRange'),
        name: SearchOrderFilterFieldNames.orderEndDateRange,
        placeholder: i18next.t('search.globalSearch.order.filterNames.orderEndDateRange'),
        version3: true,
        type: FilterCompTypes.DATERANGEV2,
        variant: 'outlined',
        secondaryLabel: i18next.t('search.globalSearch.order.filterNames.orderEndDateRange'),
        useMaxWidth: true,
        maxWidth: 210,
        clickAway: true,
        disablePortal: true,
        quickSelect: false,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.positions'),
        name: SearchOrderFilterFieldNames.positions,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: positionOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.state'),
        name: SearchOrderFilterFieldNames.state,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.statesOpts,
        selectAllValue: 'All',
        compactValues: this.filterSpecs.compactStates,
        // eslint-disable-next-line @typescript-eslint/default-param-last
        handleChangeCallback: (newValue = [], reason: AutocompleteChangeReason, { option }, onChange) => {
          if (option.value === 'All') {
            onChange(reason === 'selectOption' ? this.filterSpecs.statesOpts : []);
            return;
          }
          const compactValuesSelected = newValue.reduce(
            (results, item) => (item.isCompactOpt ? [...results, ...item.value] : results),
            [],
          );
          let newSelections = this.filterSpecs.statesOpts.reduce((results, item) => {
            let isItemSelected =
              newValue.some(x => x.value === item.value) || compactValuesSelected.some(x => x.value === item.value);

            return isItemSelected ? [...results, item] : results;
          }, []);

          if (reason === 'removeOption' && option.isCompactOpt) {
            newSelections = newSelections.reduce(
              (results, item) => (option.value.some(opt => opt.name === item.name) ? results : [...results, item]),
              [],
            );
            const compactValuesPresent = newSelections.filter(item => item?.isCompactOpt === true);
            compactValuesPresent.forEach(opt => {
              newSelections = [...newSelections, ...opt.value];
            });
            const uniqueValues = newSelections.filter((val, index, self) => {
              return self.findIndex(p => p.value === val.value) === index;
            });
            newSelections = uniqueValues;
          } else if (reason === 'removeOption' && !option.isCompactOpt) {
            const parentIndex = newSelections.findIndex(
              item => item.isCompactOpt && item.value.some(x => x.value === option.value),
            );
            parentIndex !== -1 && newSelections.splice(parentIndex, 1);

            const childIndex = newSelections.findIndex(item => item.value === option.value);
            childIndex !== -1 && newSelections.splice(childIndex, 1);
          }
          onChange(newSelections);
        },
        filterOptions: (options, { inputValue }) => {
          return dropDownFilterOptions(options, inputValue);
        },
      },
      {
        filterName: i18next.t('search.globalSearch.candidate.filterNames.city'),
        name: SearchOrderFilterFieldNames.city,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: async (key: string) => {
          if (!key) return getFilteredCities(this.filterSpecs.cityOptions, key);
          const cityOptions = await upgradeBasedOnSearchOption(key, 'c', this.gridStateFilters);
          return cityOptions;
        },
        isMultiSelect: true,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.paysGww'),
        name: SearchOrderFilterFieldNames.paysGww,
        placeholder: i18next.t('search.globalSearch.order.filterNames.paysGww'),
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: PaysGwwOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.mfs'),
        name: SearchOrderFilterFieldNames.mfs,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: MFSOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.shift'),
        name: SearchOrderFilterFieldNames.shift,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.shiftOpts,
        selectAllValue: 'All',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.accountManager'),
        name: SearchOrderFilterFieldNames.accountManager,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        options: this.filterSpecs.accountManagerOpts,
        selectAllValue: 'All',
      },
    ];
  };
}

export interface IChangeOrderIDChips {
  orderIds: IChip[];
  facilityIds: IChip[];
  divisionIds: IChip[];
  disciplineIds: IChip[];
  specialtyIds: IChip[];
  subSpecialtyIds: IChip[];
  facilityStatusIds: IChip[];
  orderTypeIds: IChip[];
  orderPriorityIds: IChip[];
  orderStartDateRange: Date;
  dateEnteredRange: any | null;
  endDateRange: any | null;
  positions: any | null;
  states: IChip[];
  city: IChip[];
  paysGww: any | null;
  maxFilesSentId: any | null;
  shiftIds: IChip[];
  amUserIds: IChip[];
}

export const initialFilterOrderChips: IChangeOrderIDChips = {
  orderIds: [],
  facilityIds: [],
  divisionIds: [],
  disciplineIds: [],
  specialtyIds: [],
  subSpecialtyIds: [],
  facilityStatusIds: [],
  orderTypeIds: [],
  orderPriorityIds: [],
  orderStartDateRange: null,
  dateEnteredRange: null,
  endDateRange: null,
  positions: null,
  states: [],
  city: [],
  paysGww: null,
  maxFilesSentId: null,
  shiftIds: [],
  amUserIds: [],
};
